import { ALL_EXPORTS_KEY } from '../api/queryKeys'
import { useExportMenusStore } from '../stores/useExportMenusStore'

/** How often a waiting Open When Ready checks whether the person is free. */
export const OPEN_WHEN_READY_RETRY_MS = 1_000

const EDITABLE_INPUT = /^(text|search|email|number|password|tel|url|date|datetime-local|time)$/

/** Whether `element` is laid out and at least partly inside the window. */
export function isOnScreen(element: HTMLElement | null): boolean {
  if (!element?.isConnected || element.getClientRects().length === 0) return false
  const rect = element.getBoundingClientRect()
  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  )
}

/**
 * Whether opening a menu now would get in the way: a dialog is open, or the
 * person is typing (editing a cell, a field, a search box).
 */
export function isPersonBusy(doc: Document = document): boolean {
  if (doc.querySelector('[role="dialog"][aria-modal="true"]')) return true
  const active = doc.activeElement
  if (!(active instanceof HTMLElement)) return false
  if (active.isContentEditable || active instanceof HTMLTextAreaElement) return true
  if (active instanceof HTMLSelectElement) return true
  return active instanceof HTMLInputElement && EDITABLE_INPUT.test(active.type || 'text')
}

/**
 * Open the Export menu nearest to an export that just finished: its own
 * table's or list's menu when that's on screen, otherwise the header's
 * Exports. False when neither is there.
 */
export function openNearestExportMenu(scopeKey: string): boolean {
  const { menus } = useExportMenusStore.getState()
  const own = menus[scopeKey]
  const target = own && isOnScreen(own.element()) ? own : menus[ALL_EXPORTS_KEY]
  if (!target || !isOnScreen(target.element())) return false
  target.open()
  return true
}

/**
 * Open When Ready: open the nearest menu now, or, while the person is busy,
 * once they're done. Returns a cancel for the wait.
 */
export function openWhenFree(
  scopeKey: string,
  { retryMs = OPEN_WHEN_READY_RETRY_MS }: { retryMs?: number } = {}
): () => void {
  if (!isPersonBusy()) {
    openNearestExportMenu(scopeKey)
    return () => {}
  }
  const timer = window.setInterval(() => {
    if (isPersonBusy()) return
    window.clearInterval(timer)
    openNearestExportMenu(scopeKey)
  }, retryMs)
  return () => window.clearInterval(timer)
}
